import { Box, Stack, Typography, useMediaQuery } from '@mui/material';
import { ComponentType, useEffect } from 'react';

import { IconButton } from '@/components/form/baseInputs/Button/IconButton';
import type { IconProps } from '@/components/icons/Microphone02Icon';
import { Microphone02Icon } from '@/components/icons/Microphone02Icon';
import { StopIcon } from '@/components/icons/StopIcon';
import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { Tooltip } from '@/components/poppers/Tooltip/Tooltip';
import { useTrackUserEvent } from '@/hooks/useTrackUserEvent';
import { COLORS } from '@/styles/tokens/colors';

import type { DictationState } from '../hooks/useHouseholdChatDictation';
import { useHouseholdChatDictation } from '../hooks/useHouseholdChatDictation';

interface HouseholdChatDictationControlProps {
  dictationTarget?: string;
  disabled?: boolean;
  idleControlSize?: number;
  idleIcon?: ComponentType<IconProps>;
  idleIconSize?: number;
  onStateChange: (state: DictationState) => void;
  onTranscript: (transcript: string) => void;
}

function formatDuration(elapsedSeconds: number) {
  const minutes = Math.floor(elapsedSeconds / 60);
  const seconds = elapsedSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

export function HouseholdChatDictationControl({
  dictationTarget = 'prompt',
  disabled = false,
  idleControlSize = 20,
  idleIcon = Microphone02Icon,
  idleIconSize = 16,
  onStateChange,
  onTranscript,
}: HouseholdChatDictationControlProps) {
  const reducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const { showFeedback } = useFeedback();
  const trackUserEvent = useTrackUserEvent();
  const { cancel, elapsedSeconds, start, state, stop, waveform } =
    useHouseholdChatDictation({
      onError: (message) => showFeedback(message, { variant: 'error' }),
      onTranscript: (transcript) => {
        trackUserEvent('household_chat voice dictation used', {
          target: dictationTarget,
          transcriptLength: transcript.length,
        });
        onTranscript(transcript);
      },
      reducedMotion,
    });

  useEffect(() => {
    onStateChange(state);
  }, [onStateChange, state]);

  useEffect(() => {
    if (state !== 'recording') {
      return;
    }
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      cancel();
    };
    window.addEventListener('keydown', handleKeyDown, true);
    return () => window.removeEventListener('keydown', handleKeyDown, true);
  }, [cancel, state]);

  if (state === 'recording') {
    return (
      <Stack
        direction="row"
        alignItems="center"
        gap={0.75}
        role="status"
        aria-label={`Recording ${dictationTarget}, ${formatDuration(elapsedSeconds)}`}
        sx={{ height: 20 }}
      >
        <Box
          aria-hidden
          sx={{
            width: 6,
            height: 6,
            borderRadius: '50%',
            bgcolor: COLORS.FUNCTIONAL.ERROR.DEFAULT,
          }}
        />
        <Stack
          aria-hidden
          direction="row"
          alignItems="center"
          gap="2px"
          sx={{ height: 20 }}
        >
          {waveform.map((level, index) => (
            <Box
              // The fixed bar positions are stable for the lifetime of a recording.
              key={index}
              sx={{
                width: 2,
                height: `${Math.round(4 + level * 12)}px`,
                borderRadius: 1,
                bgcolor: COLORS.GRAY[500],
              }}
            />
          ))}
        </Stack>
        <Typography
          variant="body-xs"
          sx={{
            display: 'flex',
            alignItems: 'center',
            height: 20,
            lineHeight: '16px',
            minWidth: 28,
          }}
        >
          {formatDuration(elapsedSeconds)}
        </Typography>
        <Tooltip placement="top" title="Stop and transcribe">
          <IconButton
            icon={StopIcon}
            iconProps={{ fill: 'currentColor' }}
            ariaLabel="Stop recording and transcribe"
            variant="transparent"
            size="xs"
            onClick={stop}
            sx={{ width: 20, height: 20, minWidth: 20, p: 0 }}
          />
        </Tooltip>
      </Stack>
    );
  }

  const isWaiting =
    state === 'requestingPermission' || state === 'transcribing';
  const label =
    state === 'transcribing'
      ? 'Transcribing recording'
      : `Dictate ${dictationTarget}`;

  return (
    <Tooltip placement="top" title={label}>
      <Box sx={{ display: 'flex' }}>
        <IconButton
          icon={idleIcon}
          iconProps={{ size: idleIconSize }}
          ariaLabel={label}
          variant="transparent"
          size="xs"
          disabled={disabled || isWaiting}
          loading={isWaiting}
          onClick={() => start().catch(() => undefined)}
          sx={{
            width: idleControlSize,
            height: idleControlSize,
            minWidth: idleControlSize,
            p: 0,
            borderRadius: '4px',
            color: COLORS.TEXT.SECONDARY,
            '&:hover': {
              bgcolor: COLORS.GRAY[100],
              borderColor: 'transparent',
              color: COLORS.TEXT.PRIMARY,
            },
            '&.Mui-disabled': { color: COLORS.TEXT.DISABLED },
          }}
        />
      </Box>
    </Tooltip>
  );
}
